/**
 * Workspace search's calls (`KLQ-20260905-001` §5a, brief 06).
 *
 * Two transports for one answer. **The stream** (`GET /search/stream`) is the ⌘K overlay and the
 * first screen of the results page: every group's top hits, lexical first, semantic appended.
 * **The page** (`GET /search`) is one group, keyset-paged, for "load more" on the results page.
 *
 * The stream goes through `api` like every other call — `parseAs: 'stream'` hands back the
 * response's `ReadableStream` instead of parsing it, so the workspace header, the CSRF cookie and
 * the one `401` refresh stay where `api/client.ts` keeps them. It is `fetch` underneath and **not
 * `EventSource`**: an `EventSource` reconnects on its own, which here would silently run a
 * finished search again, and it cannot be aborted per keystroke (§5a, client rules).
 */

import { api, ApiError, unwrap } from '@/api/client';
import type { Problem } from '@/api/client';
import type { components } from '@/api/generated/openapi';

import { readEventStream } from './readEventStream';

type Schemas = components['schemas'];

export type SearchHit = Schemas['SearchHit'];
export type SearchGroups = Schemas['SearchGroups'];
export type SearchPage = Schemas['SearchPage'];
export type SearchGroupKey = SearchHit['group'];
export type SearchStreamEvent =
  | Schemas['SearchLexicalEvent']
  | Schemas['SearchSemanticEvent']
  | Schemas['SearchSemanticUnavailableEvent']
  | Schemas['SearchDoneEvent']
  | Schemas['SearchErrorEvent'];

/**
 * The six groups, in the order the overlay and the results page draw them (brief 06, extended
 * with Nodes and Projects by the owner 2026-09-23). TWIN: `SearchGroup` in
 * `backend/src/kalqa/domains/search/schemas.py` — the vocabulary; this is only its order.
 */
export const SEARCH_GROUPS: readonly SearchGroupKey[] = [
  'drawing',
  'task',
  'comment',
  'node',
  'project',
  'person',
];

/**
 * How long a query may be. TWIN: `MAX_QUERY` in `backend/src/kalqa/domains/search/stream.py`.
 * Held here so the box refuses what the server would answer with a `422`.
 */
export const MAX_QUERY = 200;

/**
 * The query as the server reads it — whitespace collapsed, otherwise as typed
 * (`query.parse` in `backend/src/kalqa/domains/search/query.py`). Every event echoes this form,
 * so it is also the form a client compares against: comparing the raw box would drop the right
 * answer to `"rezidencia  poprad"` because the server said `"rezidencia poprad"`.
 */
export function normaliseQuery(raw: string): string {
  return raw.split(/\s+/).filter((word) => word !== '').join(' ');
}

const STREAM_EVENTS: ReadonlySet<string> = new Set([
  'lexical',
  'semantic',
  'semantic_unavailable',
  'done',
  'error',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * The narrowing the generated types cannot do for a hand-read stream. Only the discriminator is
 * checked: the payload is the server's own model, and a line that is not one of the five events
 * is skipped rather than guessed at.
 */
function isStreamEvent(value: unknown): value is SearchStreamEvent {
  return isRecord(value) && typeof value.event === 'string' && STREAM_EVENTS.has(value.event);
}

/**
 * The events of one search, in order, until the server closes the stream or `signal` aborts it.
 *
 * A refused request (`401` after the refresh, `422`) throws `ApiError` before the first event,
 * as every other call does. After the `200`, a failure arrives as an `error` event — the caller
 * reads it like any other.
 */
export async function* streamSearch(
  q: string,
  signal: AbortSignal,
): AsyncGenerator<SearchStreamEvent, void, undefined> {
  const { data, error, response } = await api.GET('/api/v1/search/stream', {
    params: { query: { q } },
    headers: { Accept: 'text/event-stream' },
    parseAs: 'stream',
    signal,
  });
  if (!response.ok || data === undefined || data === null) {
    await unwrap(Promise.resolve({ data: undefined, error, response }));
    throw new ApiError(missingBody(response));
  }
  for await (const payload of readEventStream(data, signal)) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(payload);
    } catch {
      continue;
    }
    if (isStreamEvent(parsed)) yield parsed;
  }
}

/** A `200` with no body — a proxy that ate it. Said as a problem so the caller has one shape. */
function missingBody(response: Response): Problem {
  return {
    type: 'about:blank',
    title: response.statusText || 'Request failed',
    status: response.status,
    code: 'internal_error',
  };
}

/** One group's page, lexical rows then semantic ones; `cursor` from the previous page. */
export function searchPage(
  q: string,
  group: SearchGroupKey,
  cursor: string | null,
): Promise<SearchPage> {
  return unwrap(
    api.GET('/api/v1/search', {
      params: {
        query: {
          q,
          type: group,
          ...(cursor === null ? {} : { cursor }),
        },
      },
    }),
  );
}
